import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import RefreshIcon from "@material-ui/icons/Refresh";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import GetAppIcon from "@material-ui/icons/GetApp";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ForbiddenPage from "../../components/ForbiddenPage";
import AuditLogDetailModal from "../../components/AuditLogDetailModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Collapse, Card, CardContent, Avatar, Tooltip } from "@material-ui/core";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  actionChip: {
    fontWeight: "bold",
    fontSize: "0.75rem",
    height: "24px",
  },
  createChip: {
    backgroundColor: "#10B981",
    color: "#fff",
  },
  updateChip: {
    backgroundColor: "#F59E0B",
    color: "#fff",
  },
  deleteChip: {
    backgroundColor: "#EF4444",
    color: "#fff",
  },
  filterContainer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === "dark" ? "#2d2d2d" : "#f5f5f5",
    borderRadius: theme.shape.borderRadius,
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  filterField: {
    minWidth: 150,
  },
  statsCard: {
    marginBottom: theme.spacing(2),
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "#fff",
    borderRadius: 12,
  },
  statsContent: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  statItem: {
    textAlign: "center",
  },
  statNumber: {
    fontSize: "1.8rem",
    fontWeight: "bold",
  },
  statLabel: {
    fontSize: "0.85rem",
    opacity: 0.9,
  },
  resourceTypeChip: {
    fontSize: "0.7rem",
    height: "22px",
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[800],
  },
  tableContainer: {
    maxHeight: "calc(100vh - 350px)",
    overflowY: "auto",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  smallAvatar: {
    width: 28,
    height: 28,
    fontSize: "0.8rem",
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  filterToggle: {
    marginBottom: theme.spacing(1),
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_LOGS":
      return action.payload;
    case "RESET":
      return [];
    default:
      return state;
  }
};

const AuditLogs = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [logs, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedResourceType, setSelectedResourceType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Filter options
  const [users, setUsers] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);

  // Stats
  const [stats, setStats] = useState(null);

  // Modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Export
  const [exporting, setExporting] = useState(false);

  // Fetch resource types for filter
  useEffect(() => {
    const fetchResourceTypes = async () => {
      try {
        const { data } = await api.get("/audit-logs/resource-types");
        setResourceTypes(data);
      } catch (err) {
        console.error("Error fetching resource types:", err);
      }
    };
    fetchResourceTypes();
  }, []);

  // Fetch users for filter
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsers(data);
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    };
    fetchUsers();
  }, []);

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        const { data } = await api.get("/audit-logs/stats", { params });
        setStats(data);
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
    };
    fetchStats();
  }, [startDate, endDate]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber,
        pageSize,
        searchParam,
        userId: selectedUser || undefined,
        action: selectedAction || undefined,
        resourceType: selectedResourceType || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        sortBy: "createdAt",
        sortOrder: "DESC",
      };

      const { data } = await api.get("/audit-logs", { params });
      dispatch({ type: "LOAD_LOGS", payload: data.logs });
      setHasMore(data.hasMore);
      setTotalCount(data.count);
      setTotalPages(data.totalPages);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, searchParam, selectedUser, selectedAction, selectedResourceType, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value);
    setPageNumber(1);
  };

  const handleClearFilters = () => {
    setSearchParam("");
    setSelectedUser("");
    setSelectedAction("");
    setSelectedResourceType("");
    setStartDate("");
    setEndDate("");
    setPageNumber(1);
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
  };

  const handleRefresh = () => {
    setPageNumber(1);
    fetchLogs();
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // Buscar todos os logs com os filtros atuais (sem paginação)
      const params = {
        pageNumber: 1,
        pageSize: 10000, // Limite alto para pegar todos
        searchParam,
        userId: selectedUser || undefined,
        action: selectedAction || undefined,
        resourceType: selectedResourceType || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        sortBy: "createdAt",
        sortOrder: "DESC",
      };

      const { data } = await api.get("/audit-logs", { params });

      if (!data.logs || data.logs.length === 0) {
        toast.warning("Nenhum log para exportar com os filtros selecionados.");
        return;
      }

      // Formatar dados para Excel
      const excelData = data.logs.map((log) => ({
        "Data/Hora": formatDate(log.createdAt),
        "Usuário": log.user?.name || "N/A",
        "Ação": getActionLabel(log.action),
        "Tipo de Recurso": log.resourceType,
        "Nome do Recurso": log.resourceName || "",
        "Descrição": log.description || "",
        "ID do Recurso": log.resourceId,
        "IP": log.ipAddress || "",
      }));

      // Criar workbook e worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Logs de Auditoria");

      // Ajustar largura das colunas
      const maxWidths = {
        "Data/Hora": 20,
        "Usuário": 25,
        "Ação": 15,
        "Tipo de Recurso": 20,
        "Nome do Recurso": 30,
        "Descrição": 50,
        "ID do Recurso": 15,
        "IP": 15,
      };
      worksheet["!cols"] = Object.keys(maxWidths).map((key) => ({ wch: maxWidths[key] }));

      // Gerar nome do arquivo com data
      const fileName = `audit_logs_${format(new Date(), "yyyy-MM-dd_HH-mm")}.xlsx`;

      // Download do arquivo
      XLSX.writeFile(workbook, fileName);
      toast.success(`Exportado ${data.logs.length} registros com sucesso!`);
    } catch (err) {
      console.error("Erro ao exportar:", err);
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  const getActionChipClass = (action) => {
    switch (action) {
      case "CREATE":
        return `${classes.actionChip} ${classes.createChip}`;
      case "UPDATE":
        return `${classes.actionChip} ${classes.updateChip}`;
      case "DELETE":
        return `${classes.actionChip} ${classes.deleteChip}`;
      default:
        return classes.actionChip;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      CREATE: "Criou",
      UPDATE: "Atualizou",
      DELETE: "Deletou",
    };
    return labels[action] || action;
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), "dd/MM/yyyy HH:mm:ss", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Verificar se é admin
  if (user.profile !== "admin") {
    return (
      <MainContainer>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <AuditLogDetailModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        log={selectedLog}
      />

      <MainHeader>
        <Title>Logs de Auditoria ({totalCount})</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder="Buscar..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Filtros avançados">
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon color={showFilters ? "primary" : "inherit"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Atualizar">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Exportar para Excel">
            <IconButton onClick={handleExportExcel} disabled={exporting || loading}>
              {exporting ? <CircularProgress size={24} /> : <GetAppIcon />}
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Stats Card */}
      {stats && (
        <Card className={classes.statsCard}>
          <div className={classes.statsContent}>
            <div className={classes.statItem}>
              <Typography className={classes.statNumber}>{stats.totalLogs}</Typography>
              <Typography className={classes.statLabel}>Total de Logs</Typography>
            </div>
            <div className={classes.statItem}>
              <Typography className={classes.statNumber} style={{ color: "#10B981" }}>
                {stats.byAction.CREATE}
              </Typography>
              <Typography className={classes.statLabel}>Criações</Typography>
            </div>
            <div className={classes.statItem}>
              <Typography className={classes.statNumber} style={{ color: "#F59E0B" }}>
                {stats.byAction.UPDATE}
              </Typography>
              <Typography className={classes.statLabel}>Atualizações</Typography>
            </div>
            <div className={classes.statItem}>
              <Typography className={classes.statNumber} style={{ color: "#EF4444" }}>
                {stats.byAction.DELETE}
              </Typography>
              <Typography className={classes.statLabel}>Exclusões</Typography>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper className={classes.filterContainer}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Usuário</InputLabel>
                <Select
                  value={selectedUser}
                  onChange={(e) => {
                    setSelectedUser(e.target.value);
                    setPageNumber(1);
                  }}
                  label="Usuário"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Ação</InputLabel>
                <Select
                  value={selectedAction}
                  onChange={(e) => {
                    setSelectedAction(e.target.value);
                    setPageNumber(1);
                  }}
                  label="Ação"
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="CREATE">Criação</MenuItem>
                  <MenuItem value="UPDATE">Atualização</MenuItem>
                  <MenuItem value="DELETE">Exclusão</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Recurso</InputLabel>
                <Select
                  value={selectedResourceType}
                  onChange={(e) => {
                    setSelectedResourceType(e.target.value);
                    setPageNumber(1);
                  }}
                  label="Recurso"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {resourceTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Data Início"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPageNumber(1);
                }}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Data Fim"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPageNumber(1);
                }}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                fullWidth
                size="small"
              >
                Limpar
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.tableContainer}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Data/Hora</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell align="center">Ação</TableCell>
                <TableCell>Recurso</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell align="center">Detalhes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && logs.length === 0 ? (
                <TableRowSkeleton columns={6} />
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className={classes.emptyState}>
                      <Typography variant="body1">
                        Nenhum log de auditoria encontrado.
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(log.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <div className={classes.userInfo}>
                        <Avatar className={classes.smallAvatar}>
                          {getInitials(log.user?.name)}
                        </Avatar>
                        <Typography variant="body2">{log.user?.name || "N/A"}</Typography>
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getActionLabel(log.action)}
                        className={getActionChipClass(log.action)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.resourceType}
                        className={classes.resourceTypeChip}
                        size="small"
                      />
                      {log.resourceName && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {log.resourceName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap style={{ maxWidth: 250 }}>
                        {log.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalhes">
                        <IconButton size="small" onClick={() => handleViewDetails(log)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className={classes.pagination}>
          <Typography variant="body2" color="textSecondary">
            Mostrando {logs.length} de {totalCount} registros
          </Typography>
          <div>
            <Button
              disabled={pageNumber <= 1 || loading}
              onClick={() => setPageNumber((prev) => prev - 1)}
              size="small"
            >
              Anterior
            </Button>
            <Typography variant="body2" component="span" style={{ margin: "0 16px" }}>
              Página {pageNumber} de {totalPages || 1}
            </Typography>
            <Button
              disabled={!hasMore || loading}
              onClick={() => setPageNumber((prev) => prev + 1)}
              size="small"
            >
              Próxima
            </Button>
          </div>
        </div>
      </Paper>
    </MainContainer>
  );
};

export default AuditLogs;
